import { QueryClient, UseQueryResult, useQuery } from "@tanstack/react-query";
import { VenueCarouselResponse } from "@punchup/data/models";
import {
  getVenuePageCarousel,
  getPublicVenuePageCarousel,
  publicCarouselQueryKey,
  CAROUSEL_QUERY_KEY,
} from "./api";
import { getVenuePageCarousel as getVenuePageCarouselServer } from "@punchup/data/server";
import type { Database } from "@punchup/data/supabase.types";
import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Hook to get venue page carousel configuration and items — MANAGER view
 * (unfiltered; the venue-manager dashboard's carousel settings)
 */
export function useVenuePageCarousel(
  venuePageId: string | undefined
): UseQueryResult<VenueCarouselResponse, Error> {
  return useQuery({
    queryKey: [CAROUSEL_QUERY_KEY, venuePageId],
    queryFn: () => getVenuePageCarousel(venuePageId!),
    enabled: !!venuePageId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get venue page carousel configuration and items — PUBLIC view.
 * Uses the public route + a distinct query key from the manager hook, so
 * both legs of the public query (SSR prefetch and client refetch) serve the
 * same filtered shape and never collide with the manager dashboard's cache.
 */
export function usePublicVenuePageCarousel(
  venuePageId: string | undefined
): UseQueryResult<VenueCarouselResponse, Error> {
  return useQuery({
    queryKey: publicCarouselQueryKey(venuePageId),
    queryFn: () => getPublicVenuePageCarousel(venuePageId!),
    enabled: !!venuePageId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Prefetch venue page carousel data for server-side rendering of the PUBLIC
 * venue page (hydrates usePublicVenuePageCarousel's key).
 *
 * Fetches with `forPublicDisplay` so custom-carousel show items go through
 * the same pipeline as the default carousel's shows: expired shows are
 * dropped, live Tixologi availability is merged into `is_sold_out`, and
 * password-protected/hidden events are excluded.
 */
export async function prefetchVenuePageCarousel(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  await queryClient.prefetchQuery({
    queryKey: publicCarouselQueryKey(venuePageId),
    queryFn: async () => {
      return await getVenuePageCarouselServer(supabase, venuePageId, {
        forPublicDisplay: true,
      });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
